import { Storage } from '@cobu/platform-web'
import { logger } from '../../utils/logger'

export type SessionLanguage = 'en' | 'es'

export interface ResolveUserParams {
  authToken: string
  homebodyUserUuid: string
  emailAddress: string
  firstName?: string
  lastName?: string
  phoneNumber?: string
  buildingName?: string
  cid?: string
  clientId?: string
  buildingId?: string
  propertyId?: string
  leaseStart?: string
  leaseEnd?: string
  leaseStatus?: string
  deeplink?: string
  language?: SessionLanguage
  nativePermissions?: {
    camera: boolean
    library: boolean
    calendar: boolean
  }
}

const SUPPORTED_LANGUAGES: readonly SessionLanguage[] = ['en', 'es'] as const

const normalizeLanguage = (value: unknown): SessionLanguage | undefined => {
  if (typeof value !== 'string') return undefined
  // Accept locale tags like 'es-MX' by taking the primary subtag.
  const primary = value.trim().toLowerCase().split(/[-_]/)[0]
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(primary)
    ? (primary as SessionLanguage)
    : undefined
}

export interface SessionData extends ResolveUserParams {
  token: string
  dev: boolean
}

export async function initSession(): Promise<SessionData> {
  const searchParams = new URLSearchParams(location.search)
  
  // Check URL params first (highest priority)
  const urlAuthToken = searchParams.get('authToken') || searchParams.get('jwt') || searchParams.get('token')
  const urlHomebodyUserUuid = searchParams.get('homebodyUserUuid')
  const urlEmailAddress = searchParams.get('emailAddress')
  const urlFirstName = searchParams.get('firstName')
  const urlLastName = searchParams.get('lastName')
  const urlPhoneNumber = searchParams.get('phoneNumber')
  const urlBuildingName = searchParams.get('buildingName')
  const urlCid = searchParams.get('cid')
  const urlClientId = searchParams.get('clientId')
  const urlBuildingId = searchParams.get('buildingId')
  const urlPropertyId = searchParams.get('propertyId')
  const urlLeaseStart = searchParams.get('leaseStart')
  const urlLeaseEnd = searchParams.get('leaseEnd')
  const urlLeaseStatus = searchParams.get('leaseStatus')
  const urlLink = searchParams.get('link') || searchParams.get('deeplink')
  const urlLanguage = normalizeLanguage(
    searchParams.get('locale') || searchParams.get('language') || searchParams.get('lang')
  )

  let sessionData: Partial<ResolveUserParams> | null = null
  let isDevSession = false

  // Check if we have URL params (all required fields must be present)
  if (urlAuthToken && urlHomebodyUserUuid && urlEmailAddress) {
    sessionData = {
      authToken: urlAuthToken,
      homebodyUserUuid: urlHomebodyUserUuid,
      emailAddress: urlEmailAddress,
      firstName: urlFirstName || undefined,
      lastName: urlLastName || undefined,
      phoneNumber: urlPhoneNumber || undefined,
      buildingName: urlBuildingName || undefined,
      cid: urlCid || undefined,
      clientId: urlClientId || undefined,
      buildingId: urlBuildingId || undefined,
      propertyId: urlPropertyId || undefined,
      leaseStart: urlLeaseStart || undefined,
      leaseEnd: urlLeaseEnd || undefined,
      leaseStatus: urlLeaseStatus || undefined,
      deeplink: urlLink || undefined,
      language: urlLanguage,
    }
    // Check if it's a dev token (legacy 'token' param)
    if (searchParams.get('token')) {
      isDevSession = true
    }
  }

  // If no URL params, check for postMessage
  if (!sessionData) {
    const waitForMessage = () =>
      new Promise<ResolveUserParams>((resolve, reject) => {
        const timeout = setTimeout(() => {
          window.removeEventListener('message', handleMessage)
          reject(new Error('Timeout waiting for postMessage'))
        }, 30000) // 30 second timeout

        const handleMessage = (event: MessageEvent) => {
          const data = event.data
          
          if (typeof data === 'object' && data !== null) {
            // New format requires authToken, homebodyUserUuid, and emailAddress
            if (
              typeof (data as any).authToken === 'string' &&
              typeof (data as any).homebodyUserUuid === 'string' &&
              typeof (data as any).emailAddress === 'string'
            ) {
              clearTimeout(timeout)
              window.removeEventListener('message', handleMessage)
              const hasNativePermissions = Object.prototype.hasOwnProperty.call(
                data as Record<string, unknown>,
                'nativePermissions'
              )
              resolve({
                authToken: (data as any).authToken.trim(),
                homebodyUserUuid: (data as any).homebodyUserUuid.trim(),
                emailAddress: (data as any).emailAddress.trim(),
                firstName: (data as any).firstName?.trim(),
                lastName: (data as any).lastName?.trim(),
                phoneNumber: (data as any).phoneNumber?.trim(),
                buildingName: (data as any).buildingName?.trim(),
                cid: (data as any).cid != null ? String((data as any).cid).trim() : undefined,
                clientId: (data as any).clientId != null ? String((data as any).clientId).trim() : undefined,
                buildingId: (data as any).buildingId != null ? String((data as any).buildingId).trim() : undefined,
                propertyId: (data as any).propertyId != null ? String((data as any).propertyId).trim() : undefined,
                leaseStart: (data as any).leaseStart?.trim(),
                leaseEnd: (data as any).leaseEnd?.trim(),
                leaseStatus:
                  (data as any).leaseStatus != null ? String((data as any).leaseStatus).trim() : undefined,
                deeplink: (data as any).deeplink?.trim(),
                language: normalizeLanguage(
                  (data as any).locale ?? (data as any).language
                ),
                nativePermissions: hasNativePermissions
                  ? {
                      camera: Boolean((data as any).nativePermissions?.camera),
                      library: Boolean((data as any).nativePermissions?.library),
                      calendar: Boolean((data as any).nativePermissions?.calendar),
                    }
                  : undefined,
              })
            }
          }
        }

        window.addEventListener('message', handleMessage)
      })

    try {
      sessionData = await waitForMessage()
    } catch (error) {
      logger.error('[Token] Error waiting for message:', error)
      throw error
    }
  }

  if (!sessionData || !sessionData.authToken || !sessionData.homebodyUserUuid || !sessionData.emailAddress) {
    throw new Error('Missing required fields (authToken, homebodyUserUuid, emailAddress)')
  }

  // Note: We don't store the authToken here because the resolve API call is unauthenticated
  // The token from the API response will be stored after successful resolution

  // At this point, we know all required fields are present
  const resolvedData: ResolveUserParams = {
    authToken: sessionData.authToken,
    homebodyUserUuid: sessionData.homebodyUserUuid,
    emailAddress: sessionData.emailAddress,
    firstName: sessionData.firstName,
    lastName: sessionData.lastName,
    phoneNumber: sessionData.phoneNumber,
    buildingName: sessionData.buildingName,
    cid: sessionData.cid,
    clientId: sessionData.clientId,
    buildingId: sessionData.buildingId,
    propertyId: sessionData.propertyId,
    leaseStart: sessionData.leaseStart,
    leaseEnd: sessionData.leaseEnd,
    leaseStatus: sessionData.leaseStatus,
    deeplink: sessionData.deeplink,
    language: sessionData.language,
    nativePermissions: sessionData.nativePermissions,
  }

  return {
    ...resolvedData,
    token: resolvedData.authToken,
    dev: isDevSession,
  }
}


